import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { API } from "@/lib/api";
import { FileText, Loader2, AlertTriangle, Download, ExternalLink, Globe } from "lucide-react";
import MovixLogo from "@/components/MovixLogo";

/**
 * Public contract download page. Anyone with the share token can view/download.
 * Backend: GET /api/public/contracts/{token}?lang= renders the PDF in the chosen language.
 */
export default function PublicContract() {
  const { token } = useParams();
  const [lang, setLang] = useState("en");
  const [state, setState] = useState({ status: "loading", blobUrl: null, error: null });

  useEffect(() => {
    let activeUrl = null;
    setState({ status: "loading", blobUrl: null, error: null });
    (async () => {
      try {
        const url = `${API}/public/contracts/${token}?lang=${lang}`;
        const res = await fetch(url, { credentials: "omit" });
        if (!res.ok) {
          let detail = "";
          try {
            const j = await res.json();
            detail = j.detail || "";
          } catch { /* ignore */ }
          setState({
            status: "error",
            error: res.status === 410 ? "expired" : res.status === 404 ? "invalid" : detail || "error",
            blobUrl: null,
          });
          return;
        }
        const blob = await res.blob();
        activeUrl = URL.createObjectURL(blob);
        setState({ status: "ok", blobUrl: activeUrl, error: null });
      } catch (e) {
        setState({ status: "error", error: String(e.message || e), blobUrl: null });
      }
    })();
    return () => { if (activeUrl) URL.revokeObjectURL(activeUrl); };
  }, [token, lang]);

  const LangPill = ({ code, label }) => (
    <button
      type="button"
      onClick={() => setLang(code)}
      className={`px-3 py-1.5 text-xs font-mono uppercase tracking-wider transition-colors border-r last:border-r-0 border-zinc-700 ${
        lang === code ? "bg-[#F5A623] text-white" : "text-zinc-200 hover:bg-zinc-800"
      }`}
      data-testid={`public-lang-${code}`}
    >
      {label}
    </button>
  );

  return (
    <div className="min-h-screen bg-zinc-100 flex flex-col" data-testid="public-contract-page">
      <header className="bg-[#0A0A0A] text-white">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between flex-wrap gap-3">
          <MovixLogo variant="light" size="sm" />
          <div className="flex items-center gap-3">
            <div className="inline-flex items-stretch border border-zinc-700 bg-zinc-900" data-testid="public-lang-toggle">
              <div className="px-2.5 py-1.5 text-zinc-400 inline-flex items-center gap-1">
                <Globe className="w-3 h-3" />
              </div>
              <LangPill code="pt" label="PT" />
              <LangPill code="en" label="EN" />
              <LangPill code="es" label="ES" />
            </div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-400 hidden md:block">Signed Contract</div>
          </div>
        </div>
      </header>

      <main className="flex-1 max-w-5xl mx-auto w-full p-6">
        {state.status === "loading" && (
          <div className="border border-zinc-200 bg-white p-16 text-center">
            <Loader2 className="w-8 h-8 mx-auto text-[#F5A623] animate-spin mb-3" />
            <div className="overline">Loading contract…</div>
          </div>
        )}

        {state.status === "error" && (
          <div className="border-2 border-red-200 bg-red-50 p-12 text-center" data-testid="contract-error">
            <AlertTriangle className="w-10 h-10 mx-auto text-red-500 mb-4" />
            <div className="font-display font-bold text-2xl tracking-tight mb-2">
              {state.error === "expired" ? "Link expired" : state.error === "invalid" ? "Invalid link" : "Could not load"}
            </div>
            <p className="text-sm text-zinc-600 max-w-md mx-auto">
              {state.error === "expired"
                ? "This share link has expired. Please request a new one from Movix Car Rental."
                : state.error === "invalid"
                ? "This contract link is invalid or has been revoked."
                : "We could not load the contract right now. Please try again later."}
            </p>
            <div className="mt-6 text-xs text-zinc-500">
              Need help? Call <a href="tel:+15614556277" className="text-[#F5A623] underline">(561) 455-6277</a>
            </div>
          </div>
        )}

        {state.status === "ok" && (
          <div className="space-y-4">
            <div className="border border-zinc-200 bg-white p-4 flex items-center justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-3">
                <div className="bg-[#F5A623]/10 text-[#D97706] p-2">
                  <FileText className="w-5 h-5" />
                </div>
                <div>
                  <div className="font-display font-bold tracking-tight">Movix Rental Contract</div>
                  <div className="text-xs text-zinc-500">
                    Massachusetts standard vehicle rental agreement · <span className="uppercase font-mono">{lang}</span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <a
                  href={state.blobUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1.5 border border-zinc-300 hover:border-[#F5A623] hover:text-[#D97706] text-sm px-3 py-1.5"
                  data-testid="public-open-new-tab"
                >
                  <ExternalLink className="w-3.5 h-3.5" /> Open in new tab
                </a>
                <a
                  href={state.blobUrl}
                  download={`movix-contract-${lang}.pdf`}
                  className="inline-flex items-center gap-1.5 bg-[#F5A623] hover:bg-[#D97706] text-white text-sm px-3 py-1.5"
                  data-testid="public-download"
                >
                  <Download className="w-3.5 h-3.5" /> Download
                </a>
              </div>
            </div>

            <div className="border border-zinc-200 bg-white p-2">
              <iframe
                src={state.blobUrl}
                title="Contract"
                className="w-full h-[80vh] border-0"
                data-testid="contract-iframe"
              />
            </div>
          </div>
        )}
      </main>

      <footer className="bg-zinc-900 text-zinc-400 text-xs py-4 mt-6">
        <div className="max-w-5xl mx-auto px-6 flex items-center justify-between flex-wrap gap-2">
          <div>© Movix Car Rental · Boston, Massachusetts</div>
          <div>(561) 455-6277 · movixrental@gmail.com</div>
        </div>
      </footer>
    </div>
  );
}
